/**
 * StickyNode — Editable sticky note with context menu, tags, colors, notes panel
 */

import { useState, useRef, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import NotesPanel from './NotesPanel';

const COLORS = ['#FEF3C7', '#DBEAFE', '#D1FAE5', '#FDE68A', '#FCE7F3', '#E0E7FF', '#FEE2E2', '#F3E8FF'];

export default function StickyNode({ data, selected, id }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);
  const [titleText, setTitleText] = useState(data.content || '');
  const [descText, setDescText] = useState(data.description || '');
  const [showMenu, setShowMenu] = useState(false);
  const [addingTag, setAddingTag] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [notesText, setNotesText] = useState(data.notes || '');
  const titleRef = useRef(null);
  const descRef = useRef(null);

  useEffect(() => { setTitleText(data.content || ''); }, [data.content]);
  useEffect(() => { setDescText(data.description || ''); }, [data.description]);
  useEffect(() => { setNotesText(data.notes || ''); }, [data.notes]);
  useEffect(() => { if (editingTitle && titleRef.current) titleRef.current.focus(); }, [editingTitle]);
  useEffect(() => { if (editingDesc && descRef.current) descRef.current.focus(); }, [editingDesc]);

  const commitTitle = () => {
    setEditingTitle(false);
    if (data.onContentChange) data.onContentChange(id, titleText);
  };

  const commitDesc = () => {
    setEditingDesc(false);
    if (data.onDescriptionChange) data.onDescriptionChange(id, descText);
  };

  // Keep backward compat: double-click on body starts title edit
  const editing = editingTitle || editingDesc;
  const inputRef = titleRef;

  const addTag = () => {
    if (!tagInput.trim()) { setAddingTag(false); return; }
    const newTags = [...(data.tags || []), tagInput.trim()];
    data.onTagsChange?.(id, newTags);
    setTagInput('');
    setAddingTag(false);
  };

  const removeTag = (tag) => {
    const newTags = (data.tags || []).filter(t => t !== tag);
    data.onTagsChange?.(id, newTags);
  };

  const saveNotes = (val) => {
    setNotesText(val);
    data.onNotesChange?.(id, val);
  };


  const hasNotes = !!(data.notes?.trim() || data.attachments?.length);
  const noteCount = (data.notes?.trim() ? 1 : 0) + (data.attachments?.length || 0);

  const toggleNotes = () => {
    const next = !showNotes;
    setShowNotes(next);
    if (next) data.onNotesOpen?.(id);
    else data.onNotesClose?.(id);
  };

  return (
    <div className={`rounded-lg shadow-md transition-shadow group relative ${selected ? 'ring-2 ring-blue-500 shadow-lg' : 'hover:shadow-lg'}`}
      style={{ backgroundColor: data.color || '#FEF3C7', width: data.width || 200, minHeight: data.height || 130 }}
      onDoubleClick={() => setEditingTitle(true)}>
      <Handle type="target" position={Position.Top} className="!w-2 !h-2 !bg-gray-400" />
      <Handle type="source" position={Position.Bottom} className="!w-2 !h-2 !bg-gray-400" />
      <Handle type="target" position={Position.Left} className="!w-2 !h-2 !bg-gray-400" />
      <Handle type="source" position={Position.Right} className="!w-2 !h-2 !bg-gray-400" />

      {/* Top-right actions */}
      <div className="absolute top-1 right-1 z-10 flex items-center gap-0.5">
        {/* Star/upvote */}
        <button onClick={(e) => { e.stopPropagation(); data.onStarToggle?.(id); }}
          className={`p-1 rounded transition-colors ${data.starred ? 'text-amber-500' : 'text-gray-400 opacity-0 group-hover:opacity-100'} hover:bg-black/10`}
          title={data.starred ? 'Unstar' : 'Star'}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill={data.starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
          </svg>
        </button>
        {/* Notes indicator */}
        {hasNotes && (
          <button onClick={(e) => { e.stopPropagation(); toggleNotes(); }}
            className="p-1 rounded bg-black/5 hover:bg-black/10 transition-colors" title={`${noteCount} note(s)/attachment(s)`}>
            <svg className="w-3.5 h-3.5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
          </button>
        )}
        <button onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
          className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-black/10 transition-opacity">
          <svg className="w-4 h-4 text-gray-500" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>
        </button>
        {showMenu && (
          <>
            <div className="fixed inset-0 z-20" onClick={() => setShowMenu(false)} />
            <div className="absolute right-0 top-6 w-36 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-30 text-left">
              <button onClick={() => { setEditing(true); setShowMenu(false); }}
                className="w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                Edit text
              </button>
              <button onClick={() => { toggleNotes(); setShowMenu(false); }}
                className="w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                Notes & files
              </button>
              <button onClick={() => { setAddingTag(true); setShowMenu(false); }}
                className="w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" /></svg>
                Add tag
              </button>
              <button onClick={() => { data.onExplore?.(id); setShowMenu(false); }}
                className="w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                Explore
              </button>
              <button onClick={() => { const c = COLORS[(COLORS.indexOf(data.color) + 1) % COLORS.length]; data.onColorChange?.(id, c); setShowMenu(false); }}
                className="w-full px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>
                Change color
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button onClick={() => { data.onDelete?.(id); setShowMenu(false); }}
                className="w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 flex items-center gap-2">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Delete
              </button>
            </div>
          </>
        )}
      </div>

      <div className="p-3 pt-2">
        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-1.5">
          {data.tags?.map((tag, i) => (
            <span key={i} className="inline-flex items-center gap-0.5 text-[9px] font-semibold uppercase tracking-wider text-gray-600 bg-black/5 px-1.5 py-0.5 rounded-full">
              {tag}
              <button onClick={(e) => { e.stopPropagation(); removeTag(tag); }} className="hover:text-red-500 ml-0.5">&times;</button>
            </span>
          ))}
          {addingTag && (
            <input autoFocus type="text" value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onBlur={addTag}
              onKeyDown={e => { if (e.key === 'Enter') addTag(); if (e.key === 'Escape') setAddingTag(false); }}
              className="text-[9px] bg-white/50 border border-gray-300 rounded-full px-1.5 py-0.5 w-16 outline-none"
              placeholder="Tag..." />
          )}
        </div>

        {/* Title */}
        {editingTitle ? (
          <input ref={titleRef} value={titleText}
            onChange={e => setTitleText(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === 'Escape') commitTitle(); }}
            className="w-full bg-transparent text-sm font-semibold text-gray-900 border-none outline-none mb-1"
            placeholder="Title" />
        ) : (
          <div onClick={() => setEditingTitle(true)}
            className="text-sm font-semibold text-gray-900 cursor-text mb-1 min-h-[20px]">
            {data.content || <span className="text-gray-400 italic font-normal">Click to add title</span>}
          </div>
        )}

        {/* Description */}
        {editingDesc ? (
          <textarea ref={descRef} value={descText}
            onChange={e => setDescText(e.target.value)}
            onBlur={commitDesc}
            onKeyDown={e => { if (e.key === 'Escape') commitDesc(); }}
            className="w-full bg-transparent text-xs text-gray-600 resize-none border-none outline-none leading-snug"
            style={{ minHeight: 36 }}
            placeholder="Add description..." />
        ) : (
          <div onClick={() => setEditingDesc(true)}
            className="text-xs text-gray-600 leading-snug whitespace-pre-wrap cursor-text min-h-[20px]">
            {data.description || (selected ? <span className="text-gray-400 italic">Click to add description</span> : null)}
          </div>
        )}

        {/* Notes indicator bar (when notes exist but panel is closed) */}
        {hasNotes && !showNotes && (
          <button onClick={(e) => { e.stopPropagation(); toggleNotes(); }}
            className="mt-2 w-full flex items-center gap-1.5 text-[10px] text-gray-500 bg-black/5 rounded px-2 py-1 hover:bg-black/10 transition-colors">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            {data.notes?.trim() ? 'View notes' : ''} {data.attachments?.length ? `${data.attachments.length} file(s)` : ''}
          </button>
        )}
      </div>

      {/* Color dots (when selected) */}
      {selected && !showNotes && (
        <div className="flex gap-1 px-3 pb-2">
          {COLORS.map(c => (
            <button key={c} onClick={() => data.onColorChange?.(id, c)}
              className={`w-4 h-4 rounded-full border ${data.color === c ? 'border-gray-600 ring-1 ring-gray-400' : 'border-gray-300'}`}
              style={{ backgroundColor: c }} />
          ))}
        </div>
      )}

      {/* Notes panel */}
      {showNotes && (
        <NotesPanel
          notes={data.notes}
          attachments={data.attachments}
          authors={data.authors || []}
          comments={data.comments || []}
          currentUser={data._currentUser || 'Anonymous'}
          onNotesChange={(val) => { saveNotes(val); }}
          onAttachmentsChange={(atts) => data.onAttachmentsChange?.(id, atts)}
          onAddComment={(comment) => {
            const newComments = [...(data.comments || []), comment];
            data.onCommentsChange?.(id, newComments);
            // Add author if not already in list
            const authors = [...new Set([...(data.authors || []), comment.author])];
            data.onAuthorsChange?.(id, authors);
          }}
          onClose={() => { setShowNotes(false); data.onNotesClose?.(id); }}
        />
      )}
    </div>
  );
}
